/** 'HH:MM' or 'HH:MM:SS' -> minutes since midnight. */
export function minutesOfTime(time) {
    const [h = '0', m = '0'] = time.split(':');
    return Number(h) * 60 + Number(m);
}
/** 990 -> '16:30'. 1440 stays '24:00' so a window can end at midnight. */
export function timeOfMinutes(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
/**
 * Sorted, non-overlapping, with touching spans joined: [8,9) and [9,10)
 * become [8,10). Joining matters — two adjacent hours she opened separately
 * should read as one 08:00–10:00 window, not two rows in the day editor.
 */
export function mergeSpans(spans) {
    const sorted = spans
        .filter((s) => s.endMin > s.startMin)
        .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
    const out = [];
    for (const span of sorted) {
        const last = out[out.length - 1];
        if (last && span.startMin <= last.endMin) {
            last.endMin = Math.max(last.endMin, span.endMin);
        }
        else {
            out.push({ ...span });
        }
    }
    return out;
}
/** What is left of `from` once every span in `cuts` is removed. */
export function subtractSpans(from, cuts) {
    let rest = [{ ...from }];
    for (const cut of mergeSpans(cuts)) {
        const next = [];
        for (const span of rest) {
            // No overlap — survives whole.
            if (cut.endMin <= span.startMin || cut.startMin >= span.endMin) {
                next.push(span);
                continue;
            }
            // Left remainder, then right remainder. Both may be empty, which is
            // how a cut that covers the span removes it entirely.
            if (cut.startMin > span.startMin)
                next.push({ startMin: span.startMin, endMin: cut.startMin });
            if (cut.endMin < span.endMin)
                next.push({ startMin: cut.endMin, endMin: span.endMin });
        }
        rest = next;
    }
    return rest;
}
/**
 * True when every minute of a span is already behind `now`.
 *
 * Hours that have passed can never become bookable slots, so opening them
 * writes a window that sells nothing — which is exactly how a calendar full
 * of "open" hours ends up next to a booking page saying there are none.
 * Both the grid and the endpoint ask this same question.
 */
export function spanHasPassed(span, now) {
    if (span.date < now.day)
        return true;
    if (span.date > now.day)
        return false;
    return minutesOfTime(span.endTime) <= now.minutes;
}
/**
 * Turn a selection into the smallest set of deletes and inserts that gets one
 * day to the state Diana asked for.
 *
 * Windows she did not touch keep their ids: the plan only removes a window
 * when its hours genuinely changed. That keeps existing bookings' context
 * stable and stops the day editor reshuffling on every click.
 *
 * `slotMinutes` applies to hours being OPENED. Windows of another lesson
 * length that overlap the selection lose the overlap and keep the rest, so
 * opening 16:00–17:00 as an hour lesson inside a 30-minute afternoon does
 * exactly that and leaves the rest at 30 minutes.
 */
export function planDayWindows(existing, selection, action, slotMinutes) {
    const cuts = mergeSpans(selection);
    if (cuts.length === 0)
        return { remove: [], create: [] };
    /** Current state, grouped by lesson length. */
    const buckets = new Map();
    for (const w of existing) {
        const span = { startMin: minutesOfTime(w.startTime), endMin: minutesOfTime(w.endTime) };
        buckets.set(w.slotMinutes, [...(buckets.get(w.slotMinutes) ?? []), span]);
    }
    // The selection is removed from every lesson length, whichever action it is:
    // closing means it stays gone, opening means it comes back at the chosen one.
    for (const [len, spans] of buckets) {
        buckets.set(len, mergeSpans(spans.flatMap((s) => subtractSpans(s, cuts))));
    }
    if (action === 'open') {
        buckets.set(slotMinutes, mergeSpans([...(buckets.get(slotMinutes) ?? []), ...cuts]));
    }
    // Diff the wanted state against what exists. An existing window whose hours
    // survived untouched is claimed and left alone.
    const remove = [];
    const create = [];
    const claimed = new Set();
    for (const w of existing) {
        const wanted = buckets.get(w.slotMinutes) ?? [];
        const start = minutesOfTime(w.startTime);
        const end = minutesOfTime(w.endTime);
        const match = wanted.find((s) => s.startMin === start && s.endMin === end && !claimed.has(key(w.slotMinutes, s)));
        if (match)
            claimed.add(key(w.slotMinutes, match));
        else
            remove.push(w.id);
    }
    for (const [len, spans] of buckets) {
        for (const span of spans) {
            if (claimed.has(key(len, span)))
                continue;
            create.push({
                startTime: timeOfMinutes(span.startMin),
                endTime: timeOfMinutes(span.endMin),
                slotMinutes: len,
            });
        }
    }
    return { remove, create };
}
/**
 * The same diff for spans that carry no lesson length — time off.
 *
 * `add` merges the selection in, `remove` cuts it out, splitting a block
 * whose middle is removed. Rows whose hours did not change keep their ids.
 */
export function planSpans(existing, selection, action) {
    const cuts = mergeSpans(selection);
    if (cuts.length === 0)
        return { remove: [], create: [] };
    const current = existing.map((row) => ({
        startMin: minutesOfTime(row.startTime),
        endMin: minutesOfTime(row.endTime),
    }));
    const without = mergeSpans(current.flatMap((span) => subtractSpans(span, cuts)));
    const wanted = action === 'add' ? mergeSpans([...without, ...cuts]) : without;
    const remove = [];
    const claimed = new Set();
    for (const row of existing) {
        const start = minutesOfTime(row.startTime);
        const end = minutesOfTime(row.endTime);
        const match = wanted.find((s) => s.startMin === start && s.endMin === end && !claimed.has(`${s.startMin}-${s.endMin}`));
        if (match)
            claimed.add(`${match.startMin}-${match.endMin}`);
        else
            remove.push(row.id);
    }
    const create = wanted
        .filter((s) => !claimed.has(`${s.startMin}-${s.endMin}`))
        .map((s) => ({ startTime: timeOfMinutes(s.startMin), endTime: timeOfMinutes(s.endMin) }));
    return { remove, create };
}
const key = (slotMinutes, span) => `${slotMinutes}:${span.startMin}-${span.endMin}`;
/**
 * Selected grid cells -> one span per run of touching hours, per day.
 * `Mon 16, Mon 17, Mon 19, Tue 16` becomes three spans, not four.
 */
export function spansFromCells(cells) {
    const byDay = new Map();
    for (const cell of cells) {
        const span = { startMin: cell.hour * 60, endMin: (cell.hour + 1) * 60 };
        byDay.set(cell.day, [...(byDay.get(cell.day) ?? []), span]);
    }
    return [...byDay.entries()]
        .sort(([a], [b]) => (a < b ? -1 : 1))
        .flatMap(([date, spans]) => mergeSpans(spans).map((s) => ({
        date,
        startTime: timeOfMinutes(s.startMin),
        endTime: timeOfMinutes(s.endMin),
    })));
}
